import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { getOfferItems, getTrustItems, getNavItems } from "./navData";
import SiteSearch from "./SiteSearch";

const DesktopNav = () => {
  const [openSubIndex, setOpenSubIndex] = useState<number | null>(null);
  const [openCatIndex, setOpenCatIndex] = useState<number | null>(null);
  const { lang, t } = useLanguage();
  const offerItems = getOfferItems(t, lang);
  const trustItems = getTrustItems(t, lang);
  const navItems = getNavItems(t, lang);

  return (
    <nav className="hidden lg:flex items-center justify-between w-full gap-2">
      {navItems.map((item) =>
        item.dropdown ? (
          <DropdownMenu key={item.href} modal={false} onOpenChange={() => { setOpenSubIndex(null); setOpenCatIndex(null); }}>
            <DropdownMenuTrigger className="flex items-center gap-1 text-[14px] font-medium text-primary-foreground/90 hover:text-primary-foreground transition-colors outline-none">
              {item.label}
              <ChevronDown className="w-3 h-3 ml-0.5" strokeWidth={1.5} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[240px] rounded-lg shadow-[0_6px_20px_rgba(0,0,0,0.08)] border-border/30 p-2">
              {item.dropdown === "trust" ? (
                <>
                  <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground/70 font-semibold">Reference</DropdownMenuLabel>
                  {trustItems.reference.map((sub) => (
                    <DropdownMenuItem key={sub.href} asChild>
                      <a href={sub.href} className="cursor-pointer flex items-center gap-2">
                        <sub.icon className="w-4 h-4 text-primary/70" />{sub.label}
                      </a>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground/70 font-semibold">Testimonials</DropdownMenuLabel>
                  {trustItems.testimonials.map((sub) => (
                    <DropdownMenuItem key={sub.href} asChild>
                      <a href={sub.href} className="cursor-pointer flex items-center gap-2">
                        <sub.icon className="w-4 h-4 text-primary/70" />{sub.label}
                      </a>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground/70 font-semibold">Partners</DropdownMenuLabel>
                  {trustItems.partners.map((sub) => (
                    <DropdownMenuItem key={sub.href} asChild>
                      <a href={sub.href} className="cursor-pointer flex items-center gap-2">
                        <sub.icon className="w-4 h-4 text-primary/70" />{sub.label}
                      </a>
                    </DropdownMenuItem>
                  ))}
                </>
              ) : (
                offerItems.map((sub, subIdx) =>
                  sub.subItems && sub.subItems.length > 0 ? (
                    <DropdownMenuSub key={sub.href} open={openSubIndex === subIdx} onOpenChange={(open) => { setOpenSubIndex(open ? subIdx : null); setOpenCatIndex(null); }}>
                      <DropdownMenuSubTrigger className="cursor-pointer flex items-center gap-2">
                        <sub.icon className="w-4 h-4 text-primary/70" />{sub.label}
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="min-w-[280px] rounded-lg shadow-[0_6px_20px_rgba(0,0,0,0.08)] border-border/30 p-2">
                        <DropdownMenuItem asChild>
                          <a href={sub.href} className="cursor-pointer font-semibold">
                            {lang === "en" ? "All Products" : "Vsi produkti"} →
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {sub.subItems.map((cat: any, ci: number) =>
                          cat.isCategory ? (
                            cat.subItems && cat.subItems.length > 0 ? (
                              <DropdownMenuSub key={ci} open={openCatIndex === ci} onOpenChange={(open) => setOpenCatIndex(open ? ci : null)}>
                                <DropdownMenuSubTrigger className="cursor-pointer text-sm font-semibold text-primary uppercase tracking-wider">
                                  {cat.label}
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent className="min-w-[220px] rounded-lg shadow-[0_6px_20px_rgba(0,0,0,0.08)] border-border/30 p-2">
                                  {cat.href && (
                                    <>
                                      <DropdownMenuItem asChild>
                                        <a href={cat.href} className="cursor-pointer font-semibold">
                                          {cat.label} →
                                        </a>
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                    </>
                                  )}
                                  {cat.subItems.map((child: any) =>
                                    child.isCategory && child.subItems?.length > 0 ? (
                                      <DropdownMenuSub key={child.label}>
                                        <DropdownMenuSubTrigger className="cursor-pointer text-sm">
                                          {child.label}
                                        </DropdownMenuSubTrigger>
                                        <DropdownMenuSubContent className="min-w-[200px] rounded-lg shadow-[0_6px_20px_rgba(0,0,0,0.08)] border-border/30 p-2">
                                          {child.href && (
                                            <>
                                              <DropdownMenuItem asChild>
                                                <a href={child.href} className="cursor-pointer font-semibold">
                                                  {child.label} →
                                                </a>
                                              </DropdownMenuItem>
                                              <DropdownMenuSeparator />
                                            </>
                                          )}
                                          {child.subItems.map((grandchild: any) => (
                                            <DropdownMenuItem key={grandchild.href} asChild>
                                              <a href={grandchild.href} className="cursor-pointer">{grandchild.label}</a>
                                            </DropdownMenuItem>
                                          ))}
                                        </DropdownMenuSubContent>
                                      </DropdownMenuSub>
                                    ) : (
                                      <DropdownMenuItem key={child.href || child.label} asChild>
                                        <a href={child.href} className="cursor-pointer">{child.label}</a>
                                      </DropdownMenuItem>
                                    )
                                  )}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            ) : (
                              cat.href ? (
                                <DropdownMenuItem key={ci} asChild className="font-semibold text-primary uppercase tracking-wider text-sm">
                                  <a href={cat.href} className="cursor-pointer">
                                    {cat.label}
                                  </a>
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuLabel key={ci} className="text-sm uppercase tracking-wider text-primary font-semibold">
                                  {cat.label}
                                </DropdownMenuLabel>
                              )
                            )
                          ) : (
                            <DropdownMenuItem key={cat.href} asChild>
                              <a href={cat.href} className="cursor-pointer">{cat.label}</a>
                            </DropdownMenuItem>
                          )
                        )}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  ) : (
                    <DropdownMenuItem key={sub.href} asChild>
                      <a href={sub.href} className="cursor-pointer flex items-center gap-2">
                        <sub.icon className="w-4 h-4 text-primary/70" />{sub.label}
                      </a>
                    </DropdownMenuItem>
                  )
                )
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <a
            key={item.href}
            href={item.href}
            className="text-[14px] font-medium text-primary-foreground/90 hover:text-primary-foreground transition-colors hover:underline underline-offset-4 decoration-1"
          >
            {item.label}
          </a>
        )
      )}
      <SiteSearch />
    </nav>
  );
};

export default DesktopNav;
